import { type SimpleModalVariantProps } from './simple-modal-theme';

/**
 * Utility functions and helpers for Simple Modal themes
 * Following HeroUI patterns for theme management
 */

/**
 * Common theme configurations that can be reused
 */
export const modalThemePresets = {
  // Confirmation dialogs
  confirmation: {
    variant: 'default' as const,
    size: 'sm' as const,
    backdrop: 'blur' as const,
    radius: 'lg' as const,
  },

  // Error/Alert dialogs
  alert: {
    variant: 'danger' as const,
    size: 'sm' as const,
    backdrop: 'opaque' as const,
    radius: 'md' as const,
  },

  // Success notifications
  success: {
    variant: 'success' as const,
    size: 'sm' as const,
    backdrop: 'blur' as const,
    radius: 'lg' as const,
  },

  // Warning dialogs
  warning: {
    variant: 'warning' as const,
    size: 'md' as const,
    backdrop: 'blur' as const,
    radius: 'lg' as const,
  },

  // Form modals
  form: {
    variant: 'default' as const,
    size: 'lg' as const,
    backdrop: 'opaque' as const,
    radius: 'lg' as const,
  },

  // Content display
  content: {
    variant: 'flat' as const,
    size: 'xl' as const,
    backdrop: 'blur' as const,
    radius: 'lg' as const,
  },

  // Full screen experience
  fullscreen: {
    variant: 'default' as const,
    size: 'full' as const,
    backdrop: 'opaque' as const,
    radius: 'none' as const,
  },
} as const;

/**
 * Helper to get preset theme configuration
 */
export const getModalPreset = (preset: keyof typeof modalThemePresets): SimpleModalVariantProps => {
  return modalThemePresets[preset];
};

/**
 * Helper to merge custom props with preset
 */
export const mergeModalTheme = (
  preset: keyof typeof modalThemePresets,
  overrides: Partial<SimpleModalVariantProps> = {}
): SimpleModalVariantProps => {
  return {
    ...modalThemePresets[preset],
    ...overrides,
  };
};

/**
 * Theme-specific class name generators for common patterns
 */
export const modalClassNames = {
  // Standard content modal
  contentModal: {
    base: 'max-h-[90vh] overflow-hidden',
    body: 'overflow-y-auto max-h-[60vh]',
    footer: 'border-t border-divider mt-4 pt-4',
  },

  // Confirmation dialog
  confirmationDialog: {
    base: 'text-center',
    header: 'justify-center pb-2',
    body: 'text-center px-2',
    footer: 'justify-center gap-4',
  },

  // Form modal
  formModal: {
    body: 'space-y-4',
    footer: 'border-t border-divider mt-6 pt-4',
  },

  // Alert dialog
  alertDialog: {
    base: 'text-center border-l-4 border-l-danger',
    header: 'text-danger justify-center',
    body: 'text-center',
    footer: 'justify-center',
  },

  // Success dialog
  successDialog: {
    base: 'text-center border-l-4 border-l-success',
    header: 'text-success justify-center',
    body: 'text-center',
    footer: 'justify-center',
  },
};

/**
 * Animation presets for different modal types
 */
export const modalAnimations = {
  // Smooth slide up
  slideUp: {
    initial: { y: 50, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: 50, opacity: 0 },
    transition: { duration: 0.2, ease: 'easeOut' },
  },

  // Fade in/out
  fade: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.15 },
  },

  // Scale animation
  scale: {
    initial: { scale: 0.95, opacity: 0 },
    animate: { scale: 1, opacity: 1 },
    exit: { scale: 0.95, opacity: 0 },
    transition: { duration: 0.2 },
  },

  // Slide from top
  slideDown: {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1 },
    exit: { y: -50, opacity: 0 },
    transition: { duration: 0.25, ease: 'easeOut' },
  },
};

/**
 * Helper to create a themed modal hook
 */
export const createThemedModal = (
  preset: keyof typeof modalThemePresets,
  customProps: Partial<SimpleModalVariantProps> = {}
) => {
  const themeProps = mergeModalTheme(preset, customProps);

  return {
    modalProps: themeProps,
    classNames: modalClassNames[preset as keyof typeof modalClassNames] || {},
  };
};

/**
 * Responsive size helper
 */
export const getResponsiveSize = (
  mobile: SimpleModalVariantProps['size'] = 'sm',
  desktop: SimpleModalVariantProps['size'] = 'md'
): SimpleModalVariantProps['size'] => {
  if (typeof window === 'undefined') return desktop;
  return window.innerWidth < 768 ? mobile : desktop;
};

/**
 * Dark mode detection helper
 */
export const detectDarkMode = (): boolean => {
  if (typeof window === 'undefined') return false;

  // Check for explicit theme preference
  const theme = document.documentElement.classList.contains('dark') ||
        document.documentElement.dataset.theme === 'dark';

  if (theme) return true;

  // Fallback to system preference
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
};

/**
 * Export types for easier imports
 */
export type ModalPreset = keyof typeof modalThemePresets;
export type ModalClassNames = keyof typeof modalClassNames;
export type ModalAnimation = keyof typeof modalAnimations;
